import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Box,
  Paper,
  IconButton,
  Alert,
  Fade,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Chip,
  FormHelperText,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  LocationOn,
  Home,
  Apartment,
  BusinessCenter,
  School,
  CheckCircle,
  Room,
  MyLocation,
  InfoOutlined,
  Search,
  Map as MapIcon,
  PinDrop,
  MoreHoriz,
} from '@mui/icons-material';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Import vis.gl components
import {
  APIProvider,
  Map,
  Marker,
  useMapsLibrary,
  useMap,
} from '@vis.gl/react-google-maps';

const addressTypes = [
  { value: 'Villa', label: 'Villa', icon: <Home />, color: '#FF6B6B' },
  {
    value: 'Apartment',
    label: 'Apartment',
    icon: <Apartment />,
    color: '#4ECDC4',
  },
  {
    value: 'Office',
    label: 'Office',
    icon: <BusinessCenter />,
    color: '#FFD166',
  },
  {
    value: 'Other',
    label: 'Other',
    icon: <MoreHoriz />,
    color: '#06D6A0',
  },
];

// Default center (Qatar coordinates)
const DEFAULT_CENTER = {
  lat: 25.3548,
  lng: 51.1839,
};

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '250px',
  borderRadius: '12px',
  marginTop: '16px',
};

// Helper function to parse polygon coordinates
const parsePolygonCoordinates = (locationString) => {
  if (!locationString) return [];
  try {
    const parsed = JSON.parse(locationString);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// Point in polygon detection
const isPointInPolygon = (point, polygon) => {
  if (!point || !polygon || polygon.length < 3) return false;

  const x = point.lng;
  const y = point.lat;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0],
      yi = polygon[i][1];
    const xj = polygon[j][0],
      yj = polygon[j][1];

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

// Custom Marker Component
const CustomMarker = ({ position, type = 'selected' }) => {
  const isClicked = type === 'clicked';
  const isSelected = type === 'selected';

  if (isClicked) {
    return (
      <Marker
        position={position}
        icon={{
          path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
          scale: 10,
          fillColor: '#800020',
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        }}
      />
    );
  }

  if (isSelected) {
    return (
      <Marker
        position={position}
        icon={{
          url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: { width: 45, height: 45, equals: () => false },
          anchor: { x: 22.5, y: 45 },
        }}
      />
    );
  }

  return <Marker position={position} />;
};

// Custom Google Map Component with enhanced controls
const CustomGoogleMap = ({
  center,
  zoom,
  onClick,
  selectedLocation,
  clickedLocation,
}) => {
  const map = useMap();
  const [userLocation, setUserLocation] = useState(null);

  // Center map when coordinates change
  useEffect(() => {
    if (map && center) {
      map.setCenter(center);
    }
  }, [map, center]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        style={mapContainerStyle}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={12}
        gestureHandling={'greedy'}
        disableDefaultUI={true} // Enable controls to show only specific ones
        clickableIcons={false}
        // Enable only fullscreen and zoom controls
        zoomControl={true}
        mapTypeControl={false}
        scaleControl={false}
        streetViewControl={false}
        rotateControl={false}
        fullscreenControl={true}
        // Position zoom control
        zoomControlOptions={{
          position: window.google?.maps?.ControlPosition?.RIGHT_BOTTOM || 3,
        }}
        onClick={(event) => {
          if (onClick && event.detail?.latLng) {
            onClick({
              lat: event.detail.latLng.lat,
              lng: event.detail.latLng.lng,
            });
          }
        }}
      >
        {/* Main marker for selected address */}
        {selectedLocation && (
          <CustomMarker position={selectedLocation} type="selected" />
        )}

        {/* Click location marker */}
        {clickedLocation && (
          <CustomMarker position={clickedLocation} type="clicked" />
        )}

        {/* User's current location marker */}
        {/* {userLocation && (
          <CustomMarker
            position={userLocation}
            type="current"
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#FFFFFF',
              scale: 8,
            }}
          />
        )} */}
      </Map>

      {/* Current Location Button - positioned inside map */}
      <button
        onClick={() => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const userPos = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                };
                setUserLocation(userPos);

                // Center map on user's location
                if (map) {
                  map.setCenter(userPos);
                  map.setZoom(15);
                }

                // Trigger the map click handler to fill the address
                onClick(userPos);
              },
              (error) => {
                console.error('Error getting location:', error);
                alert(
                  'Unable to get your current location. Please check your browser permissions.'
                );
              },
              {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
              }
            );
          } else {
            alert('Geolocation is not supported by your browser');
          }
        }}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: '#FFFFFF',
          border: 'none',
          borderRadius: '2px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          padding: '8px',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
        }}
        title="Find my location"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20px"
          viewBox="0 0 24 24"
          width="20px"
          fill="#4285F4"
        >
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
        </svg>
      </button>
    </div>
  );
};

// Main Address Screen Component
const AddressScreenContent = () => {
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [deliveryZone, setDeliveryZone] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [selectedType, setSelectedType] = useState('Villa');
  const [street, setStreet] = useState('');
  const [building, setBuilding] = useState('');
  const [doorNumber, setDoorNumber] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [floor, setFloor] = useState('');
  const [officeNumber, setOfficeNumber] = useState('');
  const [entranceNumber, setEntranceNumber] = useState('');
  const [apartmentRoom, setApartmentRoom] = useState('');
  const [otherDetails, setOtherDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [clickedLocation, setClickedLocation] = useState(null);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [autoFilledStreet, setAutoFilledStreet] = useState(false);
  const [autoFilledBuilding, setAutoFilledBuilding] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    address: '',
    deliveryZone: '',
    doorNumber: '',
    buildingName: '',
    building: '',
    floor: '',
    officeNumber: '',
    entranceNumber: '',
    apartmentRoom: '',
    otherDetails: '',
    selectedType: '',
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('orderId');
  const deliveryFeeId = searchParams.get('delivery_fee_id');

  const successTimerRef = useRef(null);
  const placesService = useRef(null);
  const geocoderService = useRef(null);
  const autocompleteService = useRef(null);
  const autocompleteSessionRef = useRef(null);

  // Use Maps Libraries
  const placesLibrary = useMapsLibrary('places');
  const geocodingLibrary = useMapsLibrary('geocoding');

  // Initialize services when libraries are loaded
  useEffect(() => {
    if (placesLibrary) {
      // Create a dummy map element for Places Service
      const mapDiv = document.createElement('div');
      placesService.current = new placesLibrary.PlacesService(mapDiv);

      // Initialize Autocomplete Service
      if (window.google?.maps?.places?.AutocompleteService) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    }

    if (geocodingLibrary) {
      geocoderService.current = new geocodingLibrary.Geocoder();
    }
  }, [placesLibrary, geocodingLibrary]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Fetch delivery zones from API
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await axios.get(
          'https://backend-pr-app-928105274191.me-central1.run.app/api/v1/rest/zone/paginate'
        );

        if (response.data && response.data.data) {
          const zonesFromApi = response.data.data
            .filter((zone) => zone.active)
            .map((zone) => {
              const polygon = parsePolygonCoordinates(zone.location);
              return {
                id: zone.id.toString(),
                title: zone.translation?.title || `Zone ${zone.id}`,
                value: zone.id.toString(),
                label: zone.translation?.title || `Zone ${zone.id}`,
                polygon: polygon,
                d_price: zone.d_price,
              };
            })
            .sort((a, b) => a.title.localeCompare(b.title));

          setDeliveryZones(zonesFromApi);

          if (deliveryFeeId) {
            const zoneFromUrl = zonesFromApi.find(
              (zone) => zone.id === deliveryFeeId
            );
            if (zoneFromUrl) {
              setDeliveryZone(zoneFromUrl.value);
              setZoneName(zoneFromUrl.title);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
        const defaultZones = [
          {
            id: '1',
            title: 'Downtown Area',
            value: '1',
            label: 'Downtown Area',
            polygon: [],
            d_price: '30.00',
          },
          {
            id: '2',
            title: 'Residential District',
            value: '2',
            label: 'Residential District',
            polygon: [],
            d_price: '40.00',
          },
          {
            id: '3',
            title: 'Suburban Zone',
            value: '3',
            label: 'Suburban Zone',
            polygon: [],
            d_price: '50.00',
          },
        ];
        setDeliveryZones(defaultZones);

        if (deliveryFeeId) {
          const zoneFromUrl = defaultZones.find(
            (zone) => zone.id === deliveryFeeId
          );
          if (zoneFromUrl) {
            setDeliveryZone(zoneFromUrl.value);
            setZoneName(zoneFromUrl.title);
          }
        }
      } finally {
        setLoadingZones(false);
      }
    };

    fetchZones();
  }, [deliveryFeeId]);

  const detectZone = useCallback(
    (coords) => {
      if (!coords || !deliveryZones.length) return null;

      for (const zone of deliveryZones) {
        if (zone.polygon && zone.polygon.length >= 3) {
          const isInZone = isPointInPolygon(coords, zone.polygon);
          if (isInZone) {
            return zone;
          }
        }
      }

      return null;
    },
    [deliveryZones]
  );

  const getGeocode = useCallback(async (address) => {
    return new Promise((resolve, reject) => {
      if (!geocoderService.current) {
        reject(new Error('Geocoder service not available'));
        return;
      }

      geocoderService.current.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          resolve(results);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }, []);

  const getLatLng = useCallback(async (result) => {
    const location = result.geometry.location;
    return {
      lat: location.lat(),
      lng: location.lng(),
    };
  }, []);

  const getGeocodeFromLatLng = useCallback(async (latLng) => {
    return new Promise((resolve, reject) => {
      if (!geocoderService.current) {
        reject(new Error('Geocoder service not available'));
        return;
      }

      geocoderService.current.geocode(
        { location: latLng },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Reverse geocoding failed: ${status}`));
          }
        }
      );
    });
  }, []);

  // Fetch autocomplete suggestions using Google Places AutocompleteService
  const fetchAutocompleteSuggestions = useCallback(
    async (input) => {
      if (!input || input.length < 3) {
        setAutocompleteSuggestions([]);
        return;
      }

      if (!autocompleteService.current) {
        console.error('Autocomplete service not available');
        return;
      }

      setAutocompleteLoading(true);

      try {
        // Create a new session token for billing optimization
        if (placesLibrary?.SessionToken) {
          autocompleteSessionRef.current = new placesLibrary.SessionToken();
        }

        const request = {
          input: input,
          location: new window.google.maps.LatLng(
            DEFAULT_CENTER.lat,
            DEFAULT_CENTER.lng
          ),
          radius: 50000, // 50km radius
          componentRestrictions: { country: 'qa' }, // Restrict to Qatar
          sessionToken: autocompleteSessionRef.current,
        };

        autocompleteService.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              const suggestions = predictions.map((prediction) => ({
                description: prediction.description,
                place_id: prediction.place_id,
                structuredFormatting: prediction.structured_formatting,
              }));
              setAutocompleteSuggestions(suggestions);
            } else {
              setAutocompleteSuggestions([]);
            }
            setAutocompleteLoading(false);
          }
        );
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
        setAutocompleteSuggestions([]);
        setAutocompleteLoading(false);
      }
    },
    [placesLibrary]
  );

  const handleSelect = useCallback(
    async (val, placeId = null) => {
      setSearchInput(val);
      setAddress(val);
      setClickedLocation(null);
      setAutoFilledStreet(false);
      setAutoFilledBuilding(false);
      setValidationErrors((prev) => ({ ...prev, address: '' }));
      setAutocompleteSuggestions([]);

      // Clear existing street and building
      setStreet('');
      setBuilding('');

      try {
        let results;
        let latLng;

        if (placeId && placesService.current) {
          // Get place details using place_id
          const request = {
            placeId: placeId,
            fields: [
              'formatted_address',
              'geometry',
              'address_components',
              'name',
            ],
          };

          await new Promise((resolve, reject) => {
            placesService.current.getDetails(request, (place, status) => {
              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                place
              ) {
                latLng = {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                };
                results = [place];
                resolve();
              } else {
                reject(new Error('Place details not found'));
              }
            });
          });
        } else {
          // Fallback to geocoding
          results = await getGeocode(val);
          latLng = await getLatLng(results[0]);
        }

        const newCoordinates = { lat: latLng.lat, lng: latLng.lng };
        setCoordinates(newCoordinates);

        const detectedZone = detectZone(newCoordinates);
        if (detectedZone) {
          setDeliveryZone(detectedZone.value);
          setZoneName(detectedZone.title);
        }

        setSelectedPlace({
          address: val,
          lat: newCoordinates.lat,
          lng: newCoordinates.lng,
        });

        // Try to extract street and building from address components
        let streetFound = false;
        let buildingFound = false;

        if (results[0] && results[0].address_components) {
          const addressComponents = results[0].address_components;

          // Extract street name (route)
          const streetComponent = addressComponents.find((comp) =>
            comp.types.includes('route')
          );
          if (streetComponent) {
            setStreet(streetComponent.long_name);
            setAutoFilledStreet(true);
            streetFound = true;
          }

          // Extract building number (street_number)
          const buildingComponent = addressComponents.find((comp) =>
            comp.types.includes('street_number')
          );
          if (buildingComponent) {
            setBuilding(buildingComponent.long_name);
            setAutoFilledBuilding(true);
            buildingFound = true;
          }
        }

        // Fallback: parse from address string - only if nothing found
        if (!streetFound || !buildingFound) {
          const parts = val.split(',');
          if (parts[0]) {
            const firstPart = parts[0].trim();

            // Try to extract building number
            const buildingMatch = firstPart.match(/\d+/);
            if (buildingMatch && !buildingFound) {
              setBuilding(buildingMatch[0]);
              setAutoFilledBuilding(true);
              buildingFound = true;

              // Remove building number to get street name
              const streetName = firstPart.replace(buildingMatch[0], '').trim();
              if (streetName && !streetFound) {
                setStreet(streetName);
                setAutoFilledStreet(true);
                streetFound = true;
              }
            } else if (!streetFound && firstPart) {
              setStreet(firstPart);
              setAutoFilledStreet(true);
              streetFound = true;
            }
          }
        }

        // If still no street/building found, keep them empty
        if (!streetFound) {
          setStreet('');
          setAutoFilledStreet(false);
        }
        if (!buildingFound) {
          setBuilding('');
          setAutoFilledBuilding(false);
        }
      } catch (error) {
        console.log('Error: ', error);
        // Keep street and building empty on error
        setStreet('');
        setBuilding('');
        setValidationErrors((prev) => ({
          ...prev,
          address: 'Unable to find this address. Please try another one.',
        }));
      }
    },
    [getGeocode, getLatLng, detectZone]
  );

  const handleMapClick = useCallback(
    async (latLng) => {
      if (!latLng) return;

      setClickedLocation(latLng);
      setCoordinates(latLng);
      setIsReverseGeocoding(true);
      setAutoFilledStreet(false);
      setAutoFilledBuilding(false);
      setValidationErrors((prev) => ({ ...prev, address: '' }));

      // Clear existing street and building values
      setStreet('');
      setBuilding('');

      try {
        const results = await getGeocodeFromLatLng(latLng);
        const address = results[0].formatted_address;

        setAddress(address);
        setSearchInput(address);

        const detectedZone = detectZone(latLng);
        if (detectedZone) {
          setDeliveryZone(detectedZone.value);
          setZoneName(detectedZone.title);
        }

        setSelectedPlace({
          address: address,
          lat: latLng.lat,
          lng: latLng.lng,
        });

        // Extract address components
        let streetFound = false;
        let buildingFound = false;

        if (results[0] && results[0].address_components) {
          const addressComponents = results[0].address_components;

          const streetComponent = addressComponents.find((comp) =>
            comp.types.includes('route')
          );
          if (streetComponent) {
            setStreet(streetComponent.long_name);
            setAutoFilledStreet(true);
            streetFound = true;
          }

          const buildingComponent = addressComponents.find((comp) =>
            comp.types.includes('street_number')
          );
          if (buildingComponent) {
            setBuilding(buildingComponent.long_name);
            setAutoFilledBuilding(true);
            buildingFound = true;
          }
        }

        // Fallback parsing - only if nothing found from address components
        if (!streetFound || !buildingFound) {
          const parts = address.split(',');
          if (parts[0]) {
            const firstPart = parts[0].trim();
            const buildingMatch = firstPart.match(/\d+/);
            if (buildingMatch && !buildingFound) {
              setBuilding(buildingMatch[0]);
              setAutoFilledBuilding(true);
              buildingFound = true;

              // Remove building number to get street name
              const streetName = firstPart.replace(buildingMatch[0], '').trim();
              if (streetName && !streetFound) {
                setStreet(streetName);
                setAutoFilledStreet(true);
                streetFound = true;
              }
            } else if (!streetFound && firstPart) {
              // Only set street if not empty and no street was found
              setStreet(firstPart);
              setAutoFilledStreet(true);
              streetFound = true;
            }
          }
        }

        // If still no street/building found, keep them empty
        if (!streetFound) {
          setStreet('');
          setAutoFilledStreet(false);
        }
        if (!buildingFound) {
          setBuilding('');
          setAutoFilledBuilding(false);
        }
      } catch (error) {
        console.log('Error reverse geocoding:', error);
        // Keep street and building empty on error
        setStreet('');
        setBuilding('');
        setValidationErrors((prev) => ({
          ...prev,
          address:
            'Unable to get address for this location. Please try another spot.',
        }));
      } finally {
        setIsReverseGeocoding(false);
      }
    },
    [getGeocodeFromLatLng, detectZone]
  );

  const handleSubmit = async () => {
    setValidationErrors({
      address: '',
      deliveryZone: '',
      doorNumber: '',
      buildingName: '',
      building: '',
      floor: '',
      officeNumber: '',
      entranceNumber: '',
      apartmentRoom: '',
      otherDetails: '',
      selectedType: '',
    });
    const newErrors = {
      address: '',
      deliveryZone: '',
      doorNumber: '',
      buildingName: '',
      building: '',
      floor: '',
      officeNumber: '',
      entranceNumber: '',
      apartmentRoom: '',
      otherDetails: '',
      selectedType: '',
    };

    let hasError = false;

    if (!address?.trim()) {
      newErrors.address = 'Please enter an address';
      hasError = true;
    }

    // Zone
    if (!deliveryZone) {
      newErrors.deliveryZone = 'Please select a delivery zone';
      hasError = true;
    }

    // Address Type
    if (!selectedType) {
      newErrors.selectedType = 'Please select an address type';
      hasError = true;
    }

    // Villa
    if (selectedType === 'Villa') {
      if (!building?.trim()) {
        newErrors.building = 'Building Number is required';
        hasError = true;
      }

      if (!doorNumber?.trim()) {
        newErrors.doorNumber = 'Door Number is required';
        hasError = true;
      }
    }

    // Office
    if (selectedType === 'Office') {
      if (!buildingName?.trim()) {
        newErrors.buildingName = 'Building Name is required';
        hasError = true;
      }

      if (!floor?.trim()) {
        newErrors.floor = 'Floor is required';
        hasError = true;
      }

      if (!officeNumber?.trim()) {
        newErrors.officeNumber = 'Office Number is required';
        hasError = true;
      }
    }

    // Apartment
    if (selectedType === 'Apartment') {
      if (!buildingName?.trim()) {
        newErrors.buildingName = 'Building Name is required';
        hasError = true;
      }

      if (!entranceNumber?.trim()) {
        newErrors.entranceNumber = 'Entrance Number is required';
        hasError = true;
      }

      if (!floor?.trim()) {
        newErrors.floor = 'Floor is required';
        hasError = true;
      }

      if (!apartmentRoom?.trim()) {
        newErrors.apartmentRoom = 'Apartment Room is required';
        hasError = true;
      }
    }

    // Other
    if (selectedType === 'Other') {
      if (!otherDetails?.trim()) {
        newErrors.otherDetails = 'Details are required';
        hasError = true;
      }
    }

    setValidationErrors(newErrors);

    if (hasError) {
      return;
    }

    setSaving(true);

    try {
      const selectedZone = deliveryZones.find(
        (zone) => zone.value === deliveryZone
      );
      const zoneTitle = selectedZone ? selectedZone.title : zoneName;

      let buildingData = {};

      switch (selectedType) {
        case 'Villa':
          buildingData = {
            building_number: building,
            door_number: doorNumber,
          };
          break;

        case 'Office':
          buildingData = {
            building_name: buildingName,
            floor: floor,
            office_number: officeNumber,
          };
          break;

        case 'Apartment':
          buildingData = {
            building_name: buildingName,
            entrance_number: entranceNumber,
            floor: floor,
            apartment_room: apartmentRoom,
          };
          break;

        case 'Other':
          buildingData = {
            details: otherDetails,
          };
          break;

        default:
          buildingData = {};
      }

      const params = {
        address: address,
        zone_id: Number(deliveryZone),
        zone: zoneTitle,
        delivery_title: selectedType,
        building: JSON.stringify(buildingData),
        address_type: selectedType,
        location: {
          latitude: coordinates?.lat || 0,
          longitude: coordinates?.lng || 0,
        },
        price_variation_reason: null,
      };

      const response = await axios.put(
        `https://backend-pr-app-928105274191.me-central1.run.app/api/v1/rest/orders/update-address-user/${Number(
          orderId
        )}`,
        params
      );
      if (response.status === 200 || response.status === 201) {
        const params = {
          status: 'location_ready',
        };
        await axios.post(
          `https://backend-pr-app-928105274191.me-central1.run.app/api/v1/rest/orders/${Number(
            orderId
          )}/status/change/user`,
          params
        );
        setShowSuccess(true);
        triggerConfetti();

        // Show success message for 3 seconds then redirect
        successTimerRef.current = setTimeout(() => {
          setShowSuccess(false);
          navigate(`/?orderId=${orderId}`);
        }, 3000);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating address:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update address. Please try again.';

      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);

      // Auto-hide error after 4 seconds
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 4000);
    } finally {
      setSaving(false);
    }
  };

  const triggerConfetti = () => {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '999999';
    document.body.appendChild(canvas);

    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true,
    });

    myConfetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#800020', '#ff3366', '#4CAF50'],
    });

    setTimeout(() => {
      myConfetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#800020', '#ff3366'],
      });
    }, 100);

    setTimeout(() => {
      myConfetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#4ECDC4', '#06D6A0'],
      });
    }, 200);

    setTimeout(() => {
      myConfetti({
        particleCount: 30,
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
        origin: { y: 0 },
      });
    }, 300);

    // Remove canvas after animation completes (5 seconds)
    setTimeout(() => {
      if (canvas.parentNode) {
        document.body.removeChild(canvas);
      }
    }, 5000);
  };

  const handleUseCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const latLng = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            // Clear existing values first
            setStreet('');
            setBuilding('');

            // Update coordinates and trigger map click
            setCoordinates(latLng);
            setClickedLocation(latLng);

            // Get address from coordinates and fill the form
            await handleMapClick(latLng);
          } catch (error) {
            console.log('Error getting address:', error);
            setValidationErrors((prev) => ({
              ...prev,
              address:
                'Unable to get your current location address. Please type it manually.',
            }));
          }
        },
        (error) => {
          console.log('Error getting location:', error);
          setValidationErrors((prev) => ({
            ...prev,
            address: 'Please allow location access to use this feature.',
          }));
        }
      );
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        address: 'Geolocation is not supported by your browser.',
      }));
    }
  }, [handleMapClick]);

  const handleZoneChange = (event, newValue) => {
    setDeliveryZone(newValue?.value || '');
    setZoneName(newValue?.title || '');
    setValidationErrors((prev) => ({ ...prev, deliveryZone: '' }));
  };

  // Handle autocomplete search input with debouncing
  const debouncedFetchSuggestions = useMemo(() => {
    let timeoutId;
    return (input) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchAutocompleteSuggestions(input);
      }, 300);
    };
  }, [fetchAutocompleteSuggestions]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    setValidationErrors((prev) => ({ ...prev, address: '' }));

    // Clear suggestions if input is too short
    if (value.length < 3) {
      setAutocompleteSuggestions([]);
      return;
    }

    // Fetch autocomplete suggestions with debouncing
    debouncedFetchSuggestions(value);
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: 2,
        mb: 4,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
          border: '1px solid rgba(128, 0, 32, 0.1)',
          boxShadow: '0 10px 40px rgba(128, 0, 32, 0.08)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #800020 0%, #ff3366 100%)',
          },
        }}
      >
        {/* Header */}
        <Box textAlign="center" mb={3}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #800020 0%, #ff3366 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 6px 20px rgba(128, 0, 32, 0.3)',
            }}
          >
            <LocationOn sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(90deg, #800020 0%, #ff3366 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Where should we deliver?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your address details for accurate delivery
          </Typography>
        </Box>

        {/* Address Input with Current Location */}
        <Box mb={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: '#2d3748' }}
            >
              Search Address
            </Typography>
            <InfoOutlined sx={{ fontSize: 14, ml: 0.5, color: '#718096' }} />
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Start typing your address..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            error={!!validationErrors.address}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn sx={{ color: '#800020' }} />
                </InputAdornment>
              ),
              //   endAdornment: (
              //     <InputAdornment position="end">
              //       <IconButton
              //         size="small"
              //         onClick={handleUseCurrentLocation}
              //         sx={{
              //           color: '#800020',
              //           '&:hover': { bgcolor: 'rgba(128, 0, 32, 0.08)' },
              //         }}
              //       >
              //         <MyLocation fontSize="small" />
              //       </IconButton>
              //     </InputAdornment>
              //   ),
            }}
            sx={{
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'white',
                transition: 'all 0.3s',
                '&:hover fieldset': {
                  borderColor: '#800020',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#800020',
                  borderWidth: 2,
                  boxShadow: '0 0 0 4px rgba(128, 0, 32, 0.1)',
                },
              },
            }}
          />
          {validationErrors.address && (
            <FormHelperText error sx={{ ml: 1.5 }}>
              {validationErrors.address}
            </FormHelperText>
          )}

          {/* Google Places Autocomplete Suggestions */}
          {autocompleteSuggestions.length > 0 && (
            <Paper
              elevation={2}
              sx={{
                mt: 1,
                borderRadius: 2,
                maxHeight: 200,
                overflow: 'auto',
                border: '1px solid #e2e8f0',
                zIndex: 1000,
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e0',
                  borderRadius: 3,
                },
              }}
            >
              {autocompleteSuggestions.map((suggestion, index) => (
                <Box
                  key={suggestion.place_id || index}
                  onClick={() =>
                    handleSelect(suggestion.description, suggestion.place_id)
                  }
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid #f7fafc',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      bgcolor: '#f8f9ff',
                    },
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  <Room sx={{ fontSize: 18, color: '#800020', mr: 1.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {suggestion.structuredFormatting?.main_text ||
                        suggestion.description}
                    </Typography>
                    {suggestion.structuredFormatting?.secondary_text && (
                      <Typography variant="caption" color="text.secondary">
                        {suggestion.structuredFormatting.secondary_text}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Loading indicator for autocomplete */}
          {autocompleteLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <CircularProgress size={20} sx={{ color: '#800020' }} />
              <Typography variant="caption" sx={{ ml: 1, color: '#718096' }}>
                Searching...
              </Typography>
            </Box>
          )}

          {/* Clickable Map Section */}
          <Box mt={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#2d3748',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <MapIcon sx={{ color: '#800020', mr: 1 }} />
                {clickedLocation
                  ? 'Selected Location'
                  : 'Click on Map to Select Location'}
              </Typography>
              {isReverseGeocoding && (
                <CircularProgress size={16} sx={{ color: '#800020' }} />
              )}
            </Box>

            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 2px 12px rgba(128, 0, 32, 0.15)',
                },
              }}
            >
              <CustomGoogleMap
                center={coordinates || DEFAULT_CENTER}
                zoom={coordinates ? 15 : 12}
                onClick={handleMapClick}
                selectedLocation={coordinates}
                clickedLocation={clickedLocation}
                onUseCurrentLocation={handleUseCurrentLocation}
              />

              {/* Map instructions overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <PinDrop sx={{ fontSize: 16, color: '#800020', mr: 1 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {clickedLocation
                      ? 'Location selected! Address updated above.'
                      : 'Click anywhere on the map to select a location'}
                  </Typography>
                </Paper>
              </Box>
            </Paper>

            {/* Selected Address Summary (always show when there's an address) */}
            {address && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(128, 0, 32, 0.03)',
                  border: '1px solid rgba(128, 0, 32, 0.1)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOn sx={{ color: '#800020', mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {clickedLocation && 'Confirmed Address'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.9rem' }}
                    >
                      {address}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, ml: 1 }}
          >
            Search or click on the map to select delivery location
          </Typography>
        </Box>

        {/* Delivery Zone - Searchable Dropdown */}
        <Box mb={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: '#2d3748' }}
            >
              Zone
            </Typography>
            {loadingZones && (
              <CircularProgress size={16} sx={{ color: '#800020' }} />
            )}
          </Box>

          <Autocomplete
            options={deliveryZones}
            getOptionLabel={(option) => option.title}
            value={
              deliveryZones.find((zone) => zone.value === deliveryZone) || null
            }
            onChange={handleZoneChange}
            disabled={loadingZones}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search or select a zone..."
                variant="outlined"
                required
                error={!!validationErrors.deliveryZone}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#800020' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                      boxShadow: '0 0 0 4px rgba(128, 0, 32, 0.1)',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                key={option.id}
                sx={{
                  py: 1.5,
                  borderBottom: '1px solid #f5f5f5',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'rgba(128, 0, 32, 0.04)' },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(128, 0, 32, 0.08)',
                    '&:hover': { bgcolor: 'rgba(128, 0, 32, 0.12)' },
                  },
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#800020',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, flexGrow: 1 }}
                  >
                    {option.title}
                  </Typography>
                  <Chip
                    label="Available"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      color: '#4CAF50',
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                </Box>
              </MenuItem>
            )}
            PaperComponent={(props) => (
              <Paper
                {...props}
                elevation={3}
                sx={{
                  borderRadius: 3,
                  mt: 1,
                  border: '1px solid #e2e8f0',
                  '& .MuiAutocomplete-listbox': { maxHeight: 250, py: 0 },
                }}
              />
            )}
          />
          {validationErrors.deliveryZone && (
            <FormHelperText error sx={{ ml: 1.5, mt: 0.5 }}>
              {validationErrors.deliveryZone}
            </FormHelperText>
          )}

          {deliveryZone && (
            <Fade in={!!deliveryZone}>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Chip
                  icon={<LocationOn sx={{ fontSize: 16 }} />}
                  label={`Selected: ${zoneName || 'Zone'}`}
                  onDelete={() => {
                    setDeliveryZone('');
                    setZoneName('');
                  }}
                  sx={{
                    bgcolor: 'rgba(128, 0, 32, 0.08)',
                    color: '#800020',
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': {
                      color: '#800020',
                      '&:hover': { color: '#a00030' },
                    },
                  }}
                />
              </Box>
            </Fade>
          )}
        </Box>

        <Box mb={3}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 600, color: '#2d3748' }}
          >
            Type
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            {addressTypes.map((type) => (
              <Chip
                key={type.value}
                icon={React.cloneElement(type.icon, {
                  sx: {
                    color: type.color,
                    transform:
                      selectedType === type.value ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.2s',
                  },
                })}
                label={type.label}
                onClick={() => {
                  setSelectedType(type.value);

                  setValidationErrors((prev) => ({
                    ...prev,
                    selectedType: '',
                  }));
                }}
                variant={selectedType === type.value ? 'filled' : 'outlined'}
                sx={{
                  flex: 1,
                  minWidth: '20px',
                  p: 2,
                  height: 'auto',
                  borderRadius: 3,
                  cursor: 'pointer',
                  border: `2px solid ${
                    selectedType === type.value ? type.color : '#e2e8f0'
                  }`,
                  bgcolor:
                    selectedType === type.value ? `${type.color}15` : 'white',
                  color: selectedType === type.value ? type.color : '#4a5568',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform:
                    selectedType === type.value ? 'translateY(-2px)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                  },
                  '& .MuiChip-label': {
                    px: 0,
                    fontWeight: selectedType === type.value ? 700 : 600,
                    fontSize: '0.75rem',
                    textAlign: 'center',
                  },
                  '& .MuiChip-icon': {
                    color: type.color,
                    mr: 0.5,
                    ml: 0,
                    fontSize: '1.5rem',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            ))}
          </Box>
          {validationErrors.selectedType && (
            <FormHelperText error sx={{ mt: 1 }}>
              {validationErrors.selectedType}
            </FormHelperText>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
            },
            gap: 2,
            mb: 3,
          }}
        >
          {selectedType === 'Villa' && (
            <>
              <TextField
                label="Building Number"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={building}
                onChange={(e) => {
                  setBuilding(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    building: '',
                  }));
                }}
                error={!!validationErrors.building}
                helperText={validationErrors.building}
              />

              <TextField
                label="Door Number"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={doorNumber}
                error={!!validationErrors.doorNumber}
                helperText={validationErrors.doorNumber}
                onChange={(e) => {
                  setDoorNumber(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    doorNumber: '',
                  }));
                }}
              />
            </>
          )}

          {selectedType === 'Office' && (
            <>
              <TextField
                label="Building Name"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={buildingName}
                error={!!validationErrors.buildingName}
                helperText={validationErrors.buildingName}
                onChange={(e) => {
                  setBuildingName(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    buildingName: '',
                  }));
                }}
              />

              <TextField
                label="Floor"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={floor}
                error={!!validationErrors.floor}
                helperText={validationErrors.floor}
                onChange={(e) => {
                  setFloor(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    floor: '',
                  }));
                }}
              />

              <TextField
                label="Office Number"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                  gridColumn: { xs: 'span 1', sm: 'span 2' },
                }}
                value={officeNumber}
                error={!!validationErrors.officeNumber}
                helperText={validationErrors.officeNumber}
                onChange={(e) => {
                  setOfficeNumber(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    officeNumber: '',
                  }));
                }}
              />
            </>
          )}

          {selectedType === 'Apartment' && (
            <>
              <TextField
                label="Building Name"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={buildingName}
                error={!!validationErrors.buildingName}
                helperText={validationErrors.buildingName}
                onChange={(e) => {
                  setBuildingName(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    buildingName: '',
                  }));
                }}
              />

              <TextField
                label="Entrance Number"
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={entranceNumber}
                error={!!validationErrors.entranceNumber}
                helperText={validationErrors.entranceNumber}
                onChange={(e) => {
                  setEntranceNumber(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    entranceNumber: '',
                  }));
                }}
              />

              <TextField
                label="Floor"
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={floor}
                onChange={(e) => {
                  setFloor(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    floor: '',
                  }));
                }}
                error={!!validationErrors.floor}
                helperText={validationErrors.floor}
              />

              <TextField
                label="Apartment Room"
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d5dd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#800020',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#800020',
                      borderWidth: 2,
                    },
                    '& input::placeholder': {
                      color: '#98A2B3',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#667085',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#800020',
                  },
                  '& .MuiFormLabel-asterisk': {
                    color: '#e53935',
                  },
                }}
                value={apartmentRoom}
                error={!!validationErrors.apartmentRoom}
                helperText={validationErrors.apartmentRoom}
                onChange={(e) => {
                  setApartmentRoom(e.target.value);
                  setValidationErrors((prev) => ({
                    ...prev,
                    apartmentRoom: '',
                  }));
                }}
              />
            </>
          )}

          {selectedType === 'Other' && (
            <TextField
              label="Details"
              fullWidth
              multiline
              required
              rows={3}
              value={otherDetails}
              error={!!validationErrors.otherDetails}
              helperText={validationErrors.otherDetails}
              onChange={(e) => {
                setOtherDetails(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  otherDetails: '',
                }));
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white',
                  '& fieldset': {
                    borderColor: '#d0d5dd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#800020',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#800020',
                    borderWidth: 2,
                  },
                  '& textarea::placeholder': {
                    color: '#98A2B3',
                    opacity: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#667085',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#800020',
                },
                '& .MuiFormLabel-asterisk': {
                  color: '#e53935',
                },
                gridColumn: { xs: 'span 1', sm: 'span 2' },
              }}
            />
          )}
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleSubmit}
          disabled={saving || !deliveryZone}
          sx={{
            py: 1.5,
            borderRadius: 3,
            background: deliveryZone
              ? 'linear-gradient(90deg, #800020 0%, #ff3366 100%)'
              : '#e2e8f0',
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'none',
            boxShadow: deliveryZone
              ? '0 6px 20px rgba(128, 0, 32, 0.3)'
              : 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: deliveryZone ? 'translateY(-2px)' : 'none',
              boxShadow: deliveryZone
                ? '0 12px 25px rgba(128, 0, 32, 0.4)'
                : 'none',
              background: deliveryZone
                ? 'linear-gradient(90deg, #900028 0%, #ff4777 100%)'
                : '#e2e8f0',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              background: '#e2e8f0',
              color: '#a0aec0',
              boxShadow: 'none',
            },
          }}
          startIcon={
            saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircle />
            )
          }
        >
          {saving ? 'Saving...' : 'Save Address'}
        </Button>
      </Paper>

      {/* Success Dialog with Animation */}
      <Dialog
        open={showSuccess}
        onClose={() => {}}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative',
            maxWidth: '400px',
            width: '90%',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 5,
              px: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
            }}
          >
            {/* Animated Checkmark Circle */}
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                margin: '0 auto 24px',
              }}
            >
              {/* Outer Circle Animation */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '50%',
                  border: '3px solid transparent',
                  borderTopColor: '#4CAF50',
                  borderRightColor: '#4CAF50',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />

              {/* Inner Gradient Circle */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  right: 8,
                  bottom: 8,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, #800020 0%, #ff3366 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(128, 0, 32, 0.3)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              >
                {/* Animated Checkmark */}
                <CheckCircle
                  sx={{
                    fontSize: 48,
                    color: 'white',
                    animation: 'checkmark 0.5s ease-in-out forwards',
                    '@keyframes checkmark': {
                      '0%': { transform: 'scale(0) rotate(-45deg)' },
                      '50%': { transform: 'scale(1.2) rotate(0deg)' },
                      '100%': { transform: 'scale(1) rotate(0deg)' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Success Message */}
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(90deg, #800020 0%, #ff3366 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Success!
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, fontSize: '1.1rem' }}
            >
              Address updated successfully
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#718096',
                fontWeight: 500,
                animation: 'fadeInOut 2s infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.7 },
                  '50%': { opacity: 1 },
                },
              }}
            >
              Redirecting in 3 seconds...
            </Typography>

            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}
            >
              {[1, 2, 3].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#800020',
                    opacity: 0.6,
                    animation: `bounce 1.5s infinite ${dot * 0.2}s`,
                    '@keyframes bounce': {
                      '0%, 100%': { transform: 'translateY(0)' },
                      '50%': { transform: 'translateY(-6px)' },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {snackbarOpen && (
        <Alert
          severity="error"
          onClose={() => setSnackbarOpen(false)}
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            width: '90%',
            maxWidth: '300px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(211, 47, 47, 0.2)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            animation: 'slideDown 0.3s ease-out',
            '@keyframes slideDown': {
              '0%': {
                transform: 'translateX(-50%) translateY(-20px)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateX(-50%) translateY(0)',
                opacity: 1,
              },
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      )}
    </Container>
  );
};

export default function AddressScreen() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          Google Maps API key is missing. Please check your environment
          variables.
        </Alert>
      </Container>
    );
  }

  return (
    <APIProvider
      apiKey={apiKey}
      libraries={['places', 'geocoding']}
      onLoad={() => console.log('Google Maps API loaded')}
    >
      <AddressScreenContent />
    </APIProvider>
  );
}
